/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import React, { useState, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { IDBUnit } from '@netfront/gelada-identity-library';
import { ControlledForm, EnterIcon, FormFieldProps, Input, NavigationButton, Preloader, SidebarButtons, Spacing, useControlledForm, useToast } from '@netfront/ui-library';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import { getFormFieldErrorMessage } from 'components/Forms';
import { SidebarContainer } from 'components/Shared';

import { UserGroupUnitFormProps } from './UserGroupUnitForm.interfaces';

import { useCreateGroupUnit, useDeleteGroupUnit, useUpdateGroupUnit } from '../../../../../hooks';

const UserGroupUnitForm = ({ onCancel, onUpdateGroupUnits, selectedGroupUnits, unit, unitGroupId }: UserGroupUnitFormProps) => {
  const { handleToastError, handleToastSuccess } = useToast();
  const [defaultValues, setDefaultValues] = useState<FormFieldProps>();

  const { control, handleSubmit, reset } = useControlledForm({
    defaultValues,
    resolver: yupResolver(
      yup.object().shape({
        name: yup.string().label('Name').required(),
      }),
    ),
  });
  
  const handleCancel = () => {
    reset();
    onCancel();
  };

  const { handleCreateGroupUnit, isLoading: isCreateGroupUnitLoading = false } = useCreateGroupUnit({
    onCompleted: ({ unit: returnedUnit }) => {
      onUpdateGroupUnits([...selectedGroupUnits, returnedUnit]);
      reset();
      handleToastSuccess({
        message: 'Unit added successfully!',
      });
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },  
  });

  const { handleUpdateGroupUnit, isLoading: isUpdateGroupUnitLoading = false } = useUpdateGroupUnit({
    onCompleted: ({ unit: returnedUnit }) => {
      const updatedGroupUnits = selectedGroupUnits.map((item: IDBUnit) => {
        if (item.id !== unit?.id) {
          return item;
        }
  
        return {
          ...item,
          name: returnedUnit.name,
        };
      });
      reset();
      onUpdateGroupUnits(updatedGroupUnits);
      handleToastSuccess({
        message: 'Unit updated successfully!',
      });
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },  
  });

  const { handleDeleteGroupUnit, isLoading: isDeleteGroupUnitLoading = false } = useDeleteGroupUnit({
    onCompleted: () => {
      const deletedGroupUnits = selectedGroupUnits.filter((item: IDBUnit) => item.id !== unit?.id);
      reset();
      onUpdateGroupUnits(deletedGroupUnits);
      handleToastSuccess({
        message: 'Unit deleted successfully!',
      });
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },  
  });

  const handleDeleteUnit = () => {
    if (!unit) {
      return;
    }

    void handleDeleteGroupUnit({
      unitId: unit.id,
    });
  };

  const handleUpdateUnit = (item: FormFieldProps) => {
    const { name: unitName, id: unitId } = item;

    if (unit) {
      void handleUpdateGroupUnit({
        unitId,
        unitName,
      });
      
    } else {
      void handleCreateGroupUnit({
        groupId: unitGroupId,
        unitName,
      });
    }
  };

  useEffect(() => {
    setDefaultValues({
      name: unit?.name || '',
      id: unit?.id || '',
    });
  }, [unit]);

  const isLoading = isUpdateGroupUnitLoading || isDeleteGroupUnitLoading || isCreateGroupUnitLoading;

  return (
    <SidebarContainer>
      <Preloader isLoading={isLoading} />

      <Spacing size="x-large">
        <NavigationButton
          additionalClassNames="c-sidebar-navigation-button"
          direction="previous"
          icon={EnterIcon}
          rotationCssSuffix="180"
          text="Back to units"
          onClick={handleCancel}
        />
      </Spacing>

      <ControlledForm
        callBack={(item: FormFieldProps) => {
          handleUpdateUnit(item);
        }}
        handleSubmit={handleSubmit}
        isStopPropagation
      >
        <Controller 
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <Spacing size="x-large">
              <Input
                errorMessage={getFormFieldErrorMessage(fieldState)}
                id="id_unit_name"
                labelText="Name"
                type="text"
                isLabelSideBySide
                isRequired
                {...field}
              />
            </Spacing>
          )}
        />

        <SidebarButtons
          buttonSize="xs"
          onCancel={handleCancel}
          onDelete={unit ? handleDeleteUnit: undefined}
          onSaveButtonType="submit"
        />
      </ControlledForm>
    </SidebarContainer>
  );
};

export { UserGroupUnitForm };
